import { useRef, type ReactNode } from "react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";

export type TabNavigationItem<T extends string = string> = {
  id: T;
  label: string;
  icon?: ReactNode;
  testId: string;
};

export type TabNavigationProps<T extends string = string> = {
  tabs: TabNavigationItem<T>[];
  activeTab: T;
  onTabChange: (tabId: T) => void;
  className?: string;
  ariaLabel?: string;
};

type TabNavigationTabProps<T extends string = string> = {
  tab: TabNavigationItem<T>;
  isActive: boolean;
  onClick: () => void;
  onKeyDown: (key: string) => void;
};

function TabNavigationTab<T extends string = string>({
  tab,
  isActive,
  onClick,
  onKeyDown
}: TabNavigationTabProps<T>) {
  const { isDesktop } = useResponsive();
  const baseClasses = `flex flex-1 items-center justify-center gap-2 rounded-lg px-2 py-1 font-semibold transition-all border ${isDesktop ? "text-sm" : "text-xs"}`;
  const activeClasses = "bg-blue-600 text-white shadow-md border-blue-200";
  const inactiveClasses = "text-gray-700 hover:text-blue-600 border-transparent";

  const tabClasses = `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
      e.preventDefault();
      onKeyDown(e.key);
    }
  };

  return (
    <button
      role="tab"
      type="button"
      className={tabClasses}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-testid={tab.testId}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
    >
      {tab.icon && <span aria-hidden>{tab.icon}</span>}
      <span>{tab.label}</span>
    </button>
  );
}

export function TabNavigation<T extends string = string>({
  tabs,
  activeTab,
  onTabChange,
  className = "",
  ariaLabel = "Tabs"
}: TabNavigationProps<T>) {
  const tablistRef = useRef<HTMLDivElement>(null);

  const focusTabByIndex = (index: number) => {
    const el = tablistRef.current?.children[index] as HTMLButtonElement | undefined;
    el?.focus();
  };

  const handleTabKeyDown = (key: string, currentIndex: number) => {
    const lastIndex = tabs.length - 1;
    const targetIndexByKey: Record<string, number | null> = {
      ArrowRight: currentIndex < lastIndex ? currentIndex + 1 : null,
      ArrowLeft: currentIndex > 0 ? currentIndex - 1 : null,
      Home: tabs.length > 0 ? 0 : null,
      End: tabs.length > 0 ? lastIndex : null
    };
    const targetIndex = targetIndexByKey[key];
    if (targetIndex == null) return;
    const targetTab = tabs[targetIndex];
    if (!targetTab) return;
    onTabChange(targetTab.id);
    focusTabByIndex(targetIndex);
  };

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      className={`flex h-11 w-full gap-1 rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm ${className}`}
    >
      {tabs.map((tab, index) => (
        <TabNavigationTab
          key={tab.id}
          tab={tab}
          isActive={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
          onKeyDown={key => handleTabKeyDown(key, index)}
        />
      ))}
    </div>
  );
}
